import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AmbassadorShell, { card, eyebrow, ghostBtn, gold, h1Style, ink, muted, primaryBtn, Reveal } from "./_AmbassadorShell";
import { Link } from "react-router-dom";
import { EvaVoiceTap } from "@/components/landing/EvaVoiceTap";
import { useAuth } from "@/hooks/useAuth";
import { ensureAmbassadorAccount, getAmbassadorDashboard } from "@/lib/ambassadorApi";
import { useToast } from "@/hooks/use-toast";

const REF_KEY = "eva_ref";

// Best-practice code generator: e.g. "David", "Poku" -> "DAVID-POKU-XA"
function slugFromName(s: string) {
  return s.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 12);
}
function randSuffix() {
  const a = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no confusables
  return a[Math.floor(Math.random() * a.length)] + a[Math.floor(Math.random() * a.length)];
}
function buildSuggestedCode(first: string, last: string) {
  const f = slugFromName(first);
  const l = slugFromName(last);
  if (f && l) return `${f}-${l}-${randSuffix()}`;
  if (f) return `${f}-${randSuffix()}${randSuffix()}`;
  return `EVA-${randSuffix()}${randSuffix()}`;
}

export default function AmbassadorApply() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [code, setCode] = useState("");
  const [codeTouched, setCodeTouched] = useState(false);
  const [payoutEmail, setPayoutEmail] = useState("");
  const [payoutMethod, setPayoutMethod] = useState<"paypal" | "wise" | "ach" | "stripe" | "other">("paypal");
  const [audience, setAudience] = useState("");
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState<{ id: string; code: string; status: string } | null>(null);

  useEffect(() => {
    if (!user) return;
    setPayoutEmail(user.email || "");
    const meta = user.user_metadata as { full_name?: string; given_name?: string; family_name?: string; name?: string } | undefined;
    const fn = meta?.given_name || (meta?.full_name || meta?.name || "").split(" ")[0] || "";
    const ln = meta?.family_name || (meta?.full_name || meta?.name || "").split(" ").slice(1).join(" ") || "";
    setFirstName(fn);
    setLastName(ln);
    getAmbassadorDashboard()
      .then((d) => {
        const a = d.ambassador;
        if (a && a.id && a.code) {
          setExisting({ id: a.id, code: a.code, status: a.status });
        }
      })
      .catch(() => { /* not an ambassador yet — fall through to the form */ });
  }, [user]);

  // Auto-suggest code from first+last unless user has manually edited it
  useEffect(() => {
    if (codeTouched) return;
    if (!firstName && !lastName) return;
    setCode(buildSuggestedCode(firstName, lastName));
  }, [firstName, lastName, codeTouched]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    const displayName = `${firstName.trim()} ${lastName.trim()}`.trim();
    if (!displayName) {
      toast({ title: "Name required", description: "Please enter your first and last name.", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const referredByCode = (localStorage.getItem(REF_KEY) || "").toUpperCase();
      const result = await ensureAmbassadorAccount({
        display_name: displayName,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        desired_code: code.trim().toUpperCase(),
        payout_email: payoutEmail,
        payout_method: payoutMethod,
        audience,
        referred_by_code: referredByCode || null,
      });
      const issuedCode = result.referral_code || result.ambassador?.code;
      if (!issuedCode) throw new Error("No code returned. Please try again.");
      toast({ title: "You're in.", description: `Your Insider Code is ${issuedCode}. Opening your dashboard…` });
      navigate("/ambassadors/dashboard", { replace: true });
    } catch (err) {
      toast({ title: "Couldn't submit", description: err instanceof Error ? err.message : "Try again", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  if (existing) {
    const isPending = existing.status === "pending";
    const isSuspended = existing.status === "suspended";
    return (
      <AmbassadorShell>
        <Reveal>
          <p style={eyebrow}>
            <span aria-hidden style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: "#C4823A", boxShadow: "0 0 8px #C4823A", marginRight: 8, verticalAlign: "middle" }} />
            Already applied
          </p>
          <h1 style={h1Style}>{isSuspended ? "Your account is paused." : "You're already in."}</h1>
          <p style={{ color: muted, marginBottom: 16, fontSize: 18 }}>
            Your Insider Code is <strong style={{ color: ink }}>{existing.code}</strong>. Status: <strong style={{ color: ink }}>{existing.status}</strong>.
          </p>
          {(isPending || isSuspended) && (
            <div style={{ ...card, background: "#FFF8E5", borderColor: "#E8C56A", marginBottom: 20 }}>
              <p style={{ margin: 0, color: ink, fontSize: 15, lineHeight: 1.6 }}>
                {isSuspended
                  ? <>Reach out at <strong>insiders@hireeva.co</strong> and we'll sort it out.</>
                  : <>We'll be in touch at <strong>{user?.email}</strong> shortly.</>}
              </p>
              <p style={{ margin: "10px 0 0", color: muted, fontSize: 13, lineHeight: 1.6 }}>
                While you wait — skim the <Link to="/ambassadors/terms" style={{ color: ink, textDecoration: "underline", textUnderlineOffset: 2 }}>program terms</Link>. Most questions are answered there.
              </p>
            </div>
          )}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button onClick={() => navigate("/ambassadors/dashboard")} style={primaryBtn}>Open my dashboard →</button>
            <Link to="/ambassadors/terms" style={ghostBtn}>Read the terms</Link>
          </div>
        </Reveal>
      </AmbassadorShell>
    );
  }

  return (
    <AmbassadorShell>
      <Reveal>
        <p style={eyebrow}>
          <span aria-hidden style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: "#C4823A", boxShadow: "0 0 8px #C4823A", marginRight: 8, verticalAlign: "middle" }} />
          Apply · Eva Insiders
        </p>
        <h1 style={h1Style}>Tell me <em>about you.</em></h1>
        <p style={{ color: muted, maxWidth: 640, marginBottom: 14, fontSize: 17 }}>
          Two minutes, one form. Approval is instant — your share link goes live the moment you submit.<EvaVoiceTap clipId="insider_apply" hint="who are insiders?" />
        </p>
        <p style={{ color: muted, maxWidth: 640, marginBottom: 32, fontSize: 14 }}>
          This is not a coupon program. Your link tags signups back to you, buyers pay full price, and you earn on the full revenue. Clean attribution, real money, real Circle.
        </p>
        <p style={{ color: muted, maxWidth: 640, marginBottom: 32, fontSize: 13 }}>
          <strong style={{ color: ink }}>Heads up:</strong> self-referrals don't count. Buying through your own code won't earn commission — your Circle does.
        </p>
        <p style={{ color: muted, maxWidth: 640, marginBottom: 32, fontSize: 13, fontStyle: "italic" }}>
          New to running a peer group? Read the <Link to="/ambassadors/playbook" style={{ color: ink, textDecoration: "underline", textUnderlineOffset: 2, fontStyle: "normal" }}>Circle playbook</Link> first — it's a 5-minute read.
        </p>
      </Reveal>
      <form onSubmit={onSubmit} style={{ ...card, display: "grid", gap: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="First name">
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} required style={inputStyle} autoComplete="given-name" />
          </Field>
          <Field label="Last name">
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} required style={inputStyle} autoComplete="family-name" />
          </Field>
        </div>
        <Field label="Your Insider Code">
          <input
            value={code}
            onChange={(e) => { setCodeTouched(true); setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 24)); }}
            placeholder="DAVID-POKU-XA"
            pattern="[A-Z0-9\-]{3,24}"
            required
            style={inputStyle}
          />
          <span style={{ fontSize: 12, color: muted, marginTop: 2 }}>
            Auto-generated from your name. This is your invite tag — not a discount code. Edit if you want a different one.
          </span>
          {code && (
            <div style={{ marginTop: 8, padding: "10px 12px", borderRadius: 8, background: "rgba(196,130,58,0.08)", border: `1px solid ${gold}`, fontSize: 13 }}>
              <span style={{ color: muted }}>Your share link will be </span>
              <code style={{ color: ink, fontFamily: "ui-monospace, monospace", fontWeight: 600 }}>hireeva.co/r/{code}</code>
            </div>
          )}
        </Field>
        <Field label="Payout email">
          <input type="email" value={payoutEmail} onChange={(e) => setPayoutEmail(e.target.value)} required style={inputStyle} />
        </Field>
        <Field label="Preferred payout method">
          <select value={payoutMethod} onChange={(e) => setPayoutMethod(e.target.value as typeof payoutMethod)} style={inputStyle}>
            <option value="paypal">PayPal</option>
            <option value="wise">Wise</option>
            <option value="ach">ACH (US bank)</option>
            <option value="stripe">Stripe</option>
            <option value="other">Other</option>
          </select>
          <span style={{ fontSize: 12, color: muted, marginTop: 2 }}>
            We process payouts via Stripe Connect by default — your bank, the 1st of every month. The other options are fallbacks if you prefer.
          </span>
        </Field>
        <Field label="Tell me about your network">
          <textarea value={audience} onChange={(e) => setAudience(e.target.value)} required rows={4} maxLength={1000} style={{ ...inputStyle, resize: "vertical" }} />
          <span style={{ fontSize: 12, color: muted, marginTop: 2 }}>
            Who are you bringing — fellow shop owners? An industry group? A local business network? One paragraph is plenty.
          </span>
        </Field>
        <button type="submit" disabled={busy} style={{ ...primaryBtn, opacity: busy ? 0.6 : 1, cursor: busy ? "not-allowed" : "pointer" }}>
          {busy ? "Submitting…" : "Submit application →"}
        </button>
      </form>
    </AmbassadorShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "grid", gap: 6, gridColumn: "auto" }}>
      <span style={{ ...eyebrow, color: muted, marginBottom: 0 }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "12px 14px",
  border: "1px solid rgba(0,0,0,0.14)",
  borderRadius: 10,
  background: "#fff",
  fontSize: 15,
  fontFamily: "inherit",
  color: ink,
  outline: "none",
};
